/**
 * Translation Utility for Firebase Data
 * Translates fixed values stored in Firebase (e.g., "Remote", "Full-Time") 
 * into the user's selected language at render time.
 */

// Translation mappings for common Firebase values
const translationMaps = {
    workMode: {
        'Remote': { ar: 'عن بُعد', en: 'Remote' },
        'In-Person': { ar: 'حضوري', en: 'In-Person' },
        'Hybrid': { ar: 'هجين', en: 'Hybrid' }
    },
    schedule: {
        'Full-Time': { ar: 'دوام كامل', en: 'Full-Time' },
        'Part-Time': { ar: 'دوام جزئي', en: 'Part-Time' },
        'Contract': { ar: 'عقد', en: 'Contract' },
        'Internship': { ar: 'تدريب', en: 'Internship' },
        'Freelance': { ar: 'مستقل', en: 'Freelance' }
    },
    negotiability: {
        'Negotiable': { ar: 'قابل للتفاوض', en: 'Negotiable' },
        'Non-Negotiable': { ar: 'غير قابل للتفاوض', en: 'Non-Negotiable' },
        'Fixed': { ar: 'ثابت', en: 'Fixed' }
    },
    seniority: {
        'Intern': { ar: 'متدرب', en: 'Intern' },
        'Junior': { ar: 'مبتدئ', en: 'Junior' },
        'Mid': { ar: 'متوسط', en: 'Mid' },
        'Senior': { ar: 'أقدم', en: 'Senior' },
        'Lead': { ar: 'قائد', en: 'Lead' },
        'Manager': { ar: 'مدير', en: 'Manager' },
        'Director': { ar: 'مدير تنفيذي', en: 'Director' },
        'VP': { ar: 'نائب الرئيس', en: 'VP' },
        'C-Level': { ar: 'المستوى التنفيذي', en: 'C-Level' }
    },
    status: {
        'Published': { ar: 'منشور', en: 'Published' },
        'Unpublished': { ar: 'غير منشور', en: 'Unpublished' },
        'Draft': { ar: 'مسودة', en: 'Draft' }
    }
};

/**
 * Translate a value based on its type and target language
 * @param {string} value - The value to translate (e.g., "Remote")
 * @param {string} type - The type of value (e.g., "workMode", "schedule")
 * @param {string} language - Target language code (e.g., "ar", "en")
 * @returns {string} - Translated value or original if not found
 */
export const translateValue = (value, type, language = 'en') => {
    language = language.split('-')[0];
    if (!value) return value;
    if (language === 'en') return value;

    const typeMap = translationMaps[type];
    if (!typeMap) return value;

    const translation = typeMap[value];
    if (!translation) return value;

    return translation[language] || value;
};

/**
 * Get the localized part of a multilingual string (format: "English // Arabic // French")
 * @param {string} multiLangString - String with multiple language versions
 * @param {string} language - Target language code
 * @returns {string} - The appropriate language version
 */
export const getLocalizedFromMultilang = (multiLangString, language = 'en') => {
    language = language.split('-')[0];
    if (!multiLangString) return '';

    const parts = multiLangString.split(' // ');

    switch (language) {
        case 'ar':
            return parts[1] || parts[0]; // Arabic is typically second
        case 'fr':
            return parts[2] || parts[0]; // French is typically third
        default:
            return parts[0]; // English is first
    }
};

/**
 * Translate common department names
 * @param {string} department - Department name in English
 * @param {string} language - Target language code
 * @returns {string} - Translated department name
 */
export const translateDepartment = (department, language = 'en') => {
    language = language.split('-')[0];
    if (!department || language === 'en') return department;

    const departmentTranslations = {
        'Administration': { ar: 'الإدارة' },
        'Customer Support': { ar: 'دعم العملاء' },
        'Engineering': { ar: 'الهندسة' },
        'Product Management': { ar: 'إدارة المنتج' },
        'Design / UX': { ar: 'التصميم / تجربة المستخدم' },
        'Data & Analytics': { ar: 'البيانات والتحليلات' },
        'Marketing': { ar: 'التسويق' },
        'Sales': { ar: 'المبيعات' },
        'Finance & Accounting': { ar: 'المالية والمحاسبة' },
        'Human Resources': { ar: 'الموارد البشرية' },
        'Operations': { ar: 'العمليات' },
        'IT / Systems': { ar: 'تكنولوجيا المعلومات' },
        'Security / InfoSec': { ar: 'الأمن' },
        'Legal': { ar: 'القانونية' },
        'Compliance': { ar: 'الامتثال' },
        'Procurement': { ar: 'المشتريات' },
        'Supply Chain': { ar: 'سلسلة التوريد' },
        'Logistics': { ar: 'اللوجستيات' },
        'Manufacturing': { ar: 'التصنيع' },
        'Quality Assurance': { ar: 'ضمان الجودة' },
        'Research & Development': { ar: 'البحث والتطوير' },
        'Business Development': { ar: 'تطوير الأعمال' },
        'Partnerships': { ar: 'الشراكات' },
        'Strategy': { ar: 'الاستراتيجية' },
        'Content': { ar: 'المحتوى' },
        'Communications / PR': { ar: 'الاتصالات / العلاقات العامة' },
        'Community': { ar: 'المجتمع' },
        'Facilities': { ar: 'المرافق' },
        'Real Estate': { ar: 'العقارات' },
        'Construction': { ar: 'البناء' },
        'Healthcare': { ar: 'الرعاية الصحية' },
        'Education': { ar: 'التعليم' }
    };

    return departmentTranslations[department]?.[language] || department;
};

let regionCodesByName;
const regionAliases = {
    'Antigua & Barbuda': 'AG', 'Bahamas': 'BS', 'Bosnia & Herzegovina': 'BA',
    'Cape Verde': 'CV', 'Congo - Brazzaville': 'CG', 'Congo - Kinshasa': 'CD',
    'Czech Republic': 'CZ', 'Falkland Islands': 'FK', 'Gambia': 'GM',
    'Hong Kong SAR China': 'HK', 'Macedonia': 'MK', 'Myanmar (Burma)': 'MM',
    'Pitcairn Islands': 'PN', 'São Tomé & Príncipe': 'ST',
    'Svalbard & Jan Mayen': 'SJ', 'Swaziland': 'SZ', 'Trinidad & Tobago': 'TT',
    'Vatican City': 'VA',
};

// Country values in existing company records remain English. Display names come
// from the browser's locale data, including all regions in the signup selector.
export const translateCountry = (country, language = 'en') => {
    if (!country || language.split('-')[0] !== 'ar' || typeof Intl.DisplayNames !== 'function') return country;
    if (!regionCodesByName) {
        regionCodesByName = { ...regionAliases };
        const englishNames = new Intl.DisplayNames(['en'], { type: 'region' });
        for (let first = 65; first <= 90; first += 1) {
            for (let second = 65; second <= 90; second += 1) {
                const code = String.fromCharCode(first, second);
                const name = englishNames.of(code);
                if (name !== code) regionCodesByName[name] = code;
            }
        }
    }
    const code = regionCodesByName[country];
    return code ? new Intl.DisplayNames(['ar'], { type: 'region' }).of(code) : country;
};

export default {
    translateValue,
    getLocalizedFromMultilang,
    translateDepartment
};
